import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, Crown, ShoppingBag, RefreshCw, Search, ExternalLink } from 'lucide-react';
import { adminDb } from '../lib/adminDb';

interface Purchase {
  id: string;
  email: string;
  user_id: string | null;
  copecart_order_id: string;
  copecart_product_id: string;
  product_name: string;
  payment_plan: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
}

interface UserSub {
  id: string;
  email: string;
  data: {
    name?: string;
    isPremium?: boolean;
    premiumSince?: string;
    premiumProduct?: string;
  };
  updated_at: string;
}

export const SubscriptionsPage: React.FC = () => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [users, setUsers] = useState<UserSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'purchases' | 'users'>('purchases');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [purchasesRes, usersRes] = await Promise.all([
        adminDb.from('purchases').select('*').order('created_at', { ascending: false }),
        adminDb.from('users').select('*').order('updated_at', { ascending: false }),
      ]);
      setPurchases(purchasesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const premiumUsers = users.filter(u => u.data?.isPremium);
  const totalRevenue = purchases.filter(p => p.status === 'active').reduce((sum, p) => sum + (p.amount || 0), 0);
  const filteredPurchases = purchases.filter(p =>
    !search || p.email.toLowerCase().includes(search.toLowerCase()) || p.product_name?.toLowerCase().includes(search.toLowerCase()) || p.copecart_order_id.includes(search)
  );
  const filteredUsers = users.filter(u =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) || u.data?.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><CreditCard size={24} /> Abos & Käufe</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><CreditCard size={24} /> Abos & Käufe</h1>
        <button className="btn btn-secondary" onClick={loadData}><RefreshCw size={14} /> Aktualisieren</button>
      </div>

      {/* KPI Cards */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Käufe gesamt</span>
          <span className="kpi-value">{purchases.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Umsatz (aktiv)</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>€{totalRevenue.toFixed(2)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Premium-Nutzer</span>
          <span className="kpi-value">{premiumUsers.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Conversion Rate</span>
          <span className="kpi-value">{users.length > 0 ? Math.round((premiumUsers.length / users.length) * 100) : 0}%</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button className={`btn ${tab === 'purchases' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('purchases')}>
          <ShoppingBag size={14} /> Käufe ({purchases.length})
        </button>
        <button className={`btn ${tab === 'users' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('users')}>
          <Crown size={14} /> Premium-Nutzer ({premiumUsers.length})
        </button>
      </div>

      {/* Search */}
      <div className="table-container">
        <div className="table-toolbar">
          <div className="flex gap-3 items-center">
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input className="input" placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32, width: 200 }} />
            </div>
          </div>
          <span className="text-xs text-muted">{tab === 'purchases' ? `${filteredPurchases.length} Käufe` : `${filteredUsers.length} Nutzer`}</span>
        </div>

        {tab === 'purchases' ? (
          <table>
            <thead>
              <tr>
                <th>Käufer</th>
                <th>Produkt</th>
                <th>Typ</th>
                <th>Betrag</th>
                <th>Status</th>
                <th>Datum</th>
                <th>Order-ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredPurchases.map(p => (
                <tr key={p.id}>
                  <td>
                    <div className="text-sm" style={{ fontWeight: 600 }}>{p.email}</div>
                  </td>
                  <td className="text-sm">{p.product_name || p.copecart_product_id}</td>
                  <td>
                    <span className={`badge ${p.payment_plan === 'subscription' ? 'badge-info' : 'badge-neutral'}`}>
                      {p.payment_plan === 'subscription' ? 'Abo' : 'Einmal'}
                    </span>
                  </td>
                  <td className="text-sm" style={{ fontWeight: 600 }}>€{(p.amount || 0).toFixed(2)}</td>
                  <td>
                    <span className={`badge ${p.status === 'active' ? 'badge-success' : p.status === 'refunded' ? 'badge-danger' : 'badge-neutral'}`}>
                      {p.status === 'active' ? 'Aktiv' : p.status === 'refunded' ? 'Erstattet' : p.status}
                    </span>
                  </td>
                  <td className="text-xs text-muted">
                    {new Date(p.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="text-xs text-muted" style={{ fontFamily: 'monospace' }}>{p.copecart_order_id}</td>
                </tr>
              ))}
              {filteredPurchases.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  {purchases.length === 0 ? 'Noch keine Käufe. Sobald ein Kunde über Copecart kauft, erscheinen die Daten hier.' : 'Keine Ergebnisse.'}
                </td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nutzer</th>
                <th>Status</th>
                <th>Produkt</th>
                <th>Premium seit</th>
                <th>Letzte Aktivität</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.filter(u => u.data?.isPremium).map(user => (
                <tr key={user.id}>
                  <td>
                    <div>
                      <div style={{ fontWeight: 600 }}>{user.data?.name || 'Unbekannt'}</div>
                      <div className="text-xs text-muted">{user.email}</div>
                    </div>
                  </td>
                  <td><span className="badge badge-success"><Crown size={10} /> Premium</span></td>
                  <td className="text-sm">{user.data?.premiumProduct || '—'}</td>
                  <td className="text-sm">
                    {user.data?.premiumSince ? new Date(user.data.premiumSince).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td className="text-xs text-muted">
                    {user.updated_at ? new Date(user.updated_at).toLocaleDateString('de-DE') : '—'}
                  </td>
                </tr>
              ))}
              {filteredUsers.filter(u => u.data?.isPremium).length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Noch keine Premium-Nutzer.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
